
import { defineComponent, onMounted, onUnmounted, useContext, unref, useRouter, computed } from '@nuxtjs/composition-api'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import { setupTranslationPrefixer, toDate } from '~/assets/util'
import { setupPrepareEducationTixSale, setupTix } from '~/assets/composition/tix'

export default defineComponent({
  props: {
    breadcrumbs: { type: Array, required: true },
    page: { type: Object, required: true },
  },
  setup({ page }) {
    const { isEducationCustomer, tixLoginUrl, tixBaseLoginUrl, tixUser } = setupTix()
    const { $config, localePath, route, query } = useContext()
    const router = useRouter()
    const events = computed(() => (page.events || []).filter((event) => new Date(event.eventDate) >= new Date()))
    const func = debounce(() => {
      const mobile = document.querySelector('.sticky-element-mobile')
      const mobileDisplay = window.getComputedStyle(mobile).display

      if (mobileDisplay !== 'none') {
        const elementPos = mobile.getBoundingClientRect()
        const parent = mobile.parentElement.getBoundingClientRect()

        if (parent.top < elementPos.top) {
          mobile.classList.add('i-is-stuck-on-mobile')
        } else {
          mobile.classList.remove('i-is-stuck-on-mobile')
        }
      } else {
        const element = document.querySelector('.sticky-element-desktop')
        const elementPos = element.getBoundingClientRect()
        const parent = element.parentElement.getBoundingClientRect()

        if (parent.top < elementPos.top) {
          element.classList.add('i-is-stuck-on-desktop')
        } else {
          element.classList.remove('i-is-stuck-on-desktop')
        }
      }
    }, 25)

    // Make sure that the event listener is also removed, otherwise they will accumulate
    onMounted(() => {
      window.addEventListener('scroll', func)
      // This is a hack to have the carousel re-calculate the height of the videos after they're loaded
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    })
    onUnmounted(() => window.removeEventListener('scroll', func))

    return {
      ...setupTranslationPrefixer('EducationProductionDetail'),
      educationTixSale: setupPrepareEducationTixSale(),
      educationUserEditFormUri: localePath({
        name: 'education-user-edit',
        query: {
          purchaseUrl: unref(query)?.purchaseUrl,
          returnPath: unref(route)?.path,
        },
      }),
      get,
      isEducationCustomer,
      route,
      router,
      theme: page.production?.theme,
      wwwSiteUrl: $config.wwwSiteUrl,
      tixBaseLoginUrl,
      tixLoginUrl,
      tixUser,
      toDate,
      events,
    }
  },
  data() {
    return {
      slickOptions: {
        centerMode: true,
        centerPadding: '60px',
        slidesToShow: 1,
        slidesToSlide: 1,
        initialSlide: 0,
        infinite: false,
        dots: true,
        arrows: false,
        appendDots: '.slider-navigation',
        responsive: [
          {
            breakpoint: 200,
            settings: {
              centerMode: true,
              centerPadding: '10px',
              slidesToShow: 1,
              slidesToSlide: 1,
              initialSlide: 0,
              infinite: true,
              arrows: false,
            },
          },
        ],
      },
      modalVisible: false,
    }
  },
  watch: {
    tixUser(updatedTixUser) {
      // use redirects if "purchaseUrl" query param is present and user is logged in
      if (this.route?.query?.purchaseUrl !== undefined && updatedTixUser) {
        if (this.isEducationCustomer === true) {
          // forward to "selected" tix purchase url if user has all info
          window.location.href = this.route?.query?.purchaseUrl
        } else {
          // redirect to "user update form" if user is missing school info
          this.router.push({ path: this.educationUserEditFormUri })
        }
      }
    },
  },
  mounted() {
    if (this.route?.query?.showModal === '1') {
      this.setModalVisible(true)
    }
  },
  methods: {
    toggleModalVisible() {
      this.modalVisible = !this.modalVisible
    },
    setModalVisible(visibility) {
      this.modalVisible = visibility
    },
    next() {
      this.$refs.slick.next()
    },
    prev() {
      this.$refs.slick.prev()
    },
    openLessonUp(id) {
      window.LessonUp.open(id)
    },
    handlePurchaseUrl(event) {
      // if user is not logged in yet
      if (!this.tixUser) {
        // remember the clicked purchaseUrl through a special "educationRedirectPath" for when user is redirected back from Tix
        const educationRedirectPath = event?.educationRedirectPath || null
        if (educationRedirectPath !== null) {
          return `${this.tixBaseLoginUrl}?returnUrl=${encodeURIComponent(`${this.wwwSiteUrl}${educationRedirectPath}`)}`
        }

        // event.tixBaseLoginUrl should not be empty, but use the old fallback here just in case
        // should not be used as Tixly is very finicky about query params...
        return `${this.tixLoginUrl}${encodeURIComponent(`?purchaseUrl=${event.purchaseUrl}`)}`
      }
      // manually go to user update form as a fallback, in case the redirect does not take place
      if (!this.isEducationCustomer) {
        return this.localePath({
          name: 'education-user-edit',
          query: {
            purchaseUrl: event.purchaseUrl,
            returnPath: unref(this.route)?.path,
          },
        })
      }
      return event.purchaseUrl
    },
  },
})
